import React, { useState, useEffect } from 'react';
import { Card, Form, DatePicker, Button, Typography, message, Divider, Avatar, Row, Col } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, MailOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../apps/prayer-counter/firebase';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const Settings = () => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState({
    notifications: true,
    theme: 'saffron',
    language: 'en'
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserId, getUserEmail, getUserName, getUserPhoto, user, signOut } = useAuth();

  // Smart back navigation based on where user came from
  const handleBackClick = () => {
    const from = location.state?.from;
    
    if (from && from.pathname) {
      navigate(from.pathname);
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const loadPreferences = async () => {
      const userId = getUserId();
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('📖 Loading user preferences...');
        
        const docRef = doc(db, `users/${userId}/profile`);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPreferences(data.preferences || {
            notifications: true,
            theme: 'saffron',
            language: 'en'
          });
          
          // Only set date of birth if it exists
          if (data.dateOfBirth) {
            form.setFieldsValue({
              dateOfBirth: dayjs(data.dateOfBirth.toDate())
            });
          }
          
          console.log('✅ User preferences loaded');
        } else {
          console.log('ℹ️ No user profile found - using defaults (normal for new users)');
          // This is completely normal for new users - no error needed
        }
      } catch (error) {
        console.error('💥 Error loading preferences:', error);
        
        // Only show error messages for actual failures, not missing documents
        if (error.code === 'unavailable') {
          message.error('Service temporarily unavailable. Please try again.');
        } else if (error.code === 'permission-denied') {
          message.error('Permission denied. Please check your authentication.');
        } else if (error.message && error.message.includes('network')) {
          message.error('Network error. Please check your connection.');
        }
        // For other errors (like not-found), don't show anything - it's normal
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [getUserId, form]);

  const handleSave = async (values) => {
    const userId = getUserId();
    if (!userId) {
      message.error('User not authenticated');
      return;
    }
    
    try {
      setSaving(true);
      console.log('💾 Saving preferences:', values);
      
      const profileUpdate = {
        uid: userId,
        // Use Google account data directly
        email: getUserEmail(),
        displayName: getUserName(),
        photoURL: getUserPhoto(),
        emailVerified: user?.emailVerified || false,
        provider: 'google',
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(), // Will be ignored if document exists
        lastLoginAt: serverTimestamp(),
        // Only save user's actual preferences
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toDate() : null,
        preferences: {
          ...preferences,
          theme: 'saffron',
          notifications: true,
          language: 'en'
        }
      };
      
      await setDoc(doc(db, `users/${userId}/profile`), profileUpdate, { merge: true });
      
      message.success('Preferences saved successfully! 🎉');
      console.log('✅ Preferences updated successfully');
      
    } catch (error) {
      console.error('💥 Error updating preferences:', error);
      
      if (error.code === 'permission-denied') {
        message.error('Permission denied. Please check your authentication.');
      } else if (error.code === 'unavailable') {
        message.error('Service temporarily unavailable. Please try again.');
      } else if (error.message.includes('network')) {
        message.error('Network error. Please check your connection.');
      } else {
        message.error(`Error saving preferences: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
          <Text style={{ color: '#FF8C00', fontSize: '16px' }}>Loading...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(180deg, #FFF8F0 0%, #FFFFFF 100%)',
      minHeight: '100vh',
      padding: '16px'
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* Header */}
        <Card
          style={{ 
            marginBottom: 16,
            background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE4B5 100%)',
            border: '1px solid #FFB347',
            borderRadius: '12px'
          }}
          styles={{ body: { padding: '16px' } }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={handleBackClick}
                style={{ 
                  borderColor: '#FF8C00',
                  color: '#FF8C00'
                }}
              >
                Back
              </Button>
              <div>
                <Title level={4} style={{ margin: 0, color: '#FF8C00' }}>
                  ⚙️ Profile & Settings
                </Title>
                <Text style={{ color: '#FF7F00', fontSize: '14px' }}>
                  Manage your spiritual journey preferences
                </Text>
              </div>
            </div>
            <Button
              danger
              icon={<LogoutOutlined />}
              onClick={handleSignOut}
              style={{ 
                borderColor: '#ff7875',
                color: '#ff7875'
              }}
            >
              Sign Out
            </Button>
          </div>
        </Card>

        {/* Google Account Info */}
        <Card
          title={
            <span style={{ color: '#FF8C00', fontWeight: '500' }}>
              👤 Your Profile
            </span>
          }
          style={{ 
            borderRadius: '12px',
            border: '1px solid #FFB347',
            marginBottom: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '8px 0' }}>
            <Avatar 
              src={getUserPhoto()} 
              size={64}
              style={{ backgroundColor: '#FF8C00' }}
            >
              {getUserName().charAt(0).toUpperCase()}
            </Avatar>
            <div style={{ flex: 1 }}>
              <Title level={5} style={{ margin: 0, color: '#FF8C00' }}>
                {getUserName()}
              </Title>
              <Text style={{ color: '#666', display: 'block' }}>
                <MailOutlined style={{ marginRight: '8px' }} />
                {getUserEmail()}
              </Text>
              <Text style={{ 
                color: user?.emailVerified ? '#52c41a' : '#faad14', 
                fontSize: '12px',
                display: 'block',
                marginTop: '4px'
              }}>
                {user?.emailVerified ? '✅ Verified Google Account' : '⚠️ Email not verified'}
              </Text>
            </div>
          </div>
          <div style={{ 
            marginTop: '12px', 
            padding: '8px', 
            background: '#f9f9f9', 
            borderRadius: '8px',
            fontSize: '12px',
            color: '#666'
          }}>
            💡 Your profile is automatically synced from your Google account
          </div>
        </Card>

        {/* Personal Preferences */}
        <Card
          title={
            <span style={{ color: '#FF8C00', fontWeight: '500' }}>
              🔧 Personal Preferences
            </span>
          }
          style={{ 
            borderRadius: '12px',
            border: '1px solid #FFB347',
            marginBottom: '16px'
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            style={{ paddingTop: '8px' }}
          >
            <Form.Item
              name="dateOfBirth"
              label={<span style={{ color: '#FF8C00', fontWeight: '500' }}>Date of Birth (Optional)</span>}
              help="This helps us provide age-appropriate spiritual content"
            >
              <DatePicker 
                style={{ width: '100%', height: '44px' }}
                placeholder="Select your date of birth"
                format="DD/MM/YYYY"
                disabledDate={(current) => current && current > dayjs().endOf('day')}
              />
            </Form.Item>

            <Form.Item style={{ marginTop: '24px', marginBottom: '0' }}>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={saving}
                icon={<SaveOutlined />}
                style={{
                  height: '44px',
                  background: 'linear-gradient(135deg, #FF8C00 0%, #FF7F00 100%)',
                  borderColor: '#FF8C00',
                  fontWeight: '500'
                }}
                block
                disabled={!user}
              >
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* App Information */}
        <Card
          title={
            <span style={{ color: '#FF8C00', fontWeight: '500' }}>
              📱 App Information
            </span>
          }
          style={{ 
            borderRadius: '12px',
            border: '1px solid #FFB347'
          }}
        >
          <div style={{ padding: '8px 0' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
                  <Text strong style={{ color: '#FF8C00', display: 'block' }}>Secure</Text>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    Protected by Google Firebase
                  </Text>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>☁️</div>
                  <Text strong style={{ color: '#FF8C00', display: 'block' }}>Synced</Text>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    Available on all devices
                  </Text>
                </div>
              </Col>
            </Row>
            
            <Divider />
            
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🙏</div>
              <Text style={{ 
                color: '#FF8C00', 
                fontSize: '14px',
                fontStyle: 'italic'
              }}>
                "Simplicity is the ultimate sophistication in spiritual practice"
              </Text>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Settings; 